import { useAuth } from "@clerk/nextjs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import path from "constants/path";
import {
  convertDurationToDays,
  getQueryFromFilters,
} from "features/FilterDrawer/utils/queryParams/filterQueryParams";
import { THREADS_QUERY_KEY } from "features/Sidebar/hooks/useThreads";
import {
  parseThreadResponse,
  Thread,
  ThreadInteraction,
} from "features/Thread/types";
import {
  forkThread as forkThreadAPI,
  ThreadInteractionOverrides,
  ProAgentMode,
} from "helpers/api";
import { FilterParams, threadPath } from "helpers/pageUrl";
import useProToggle from "hooks/useProToggle";
import useSearchMode from "hooks/useSearchMode";
import {
  getAddThreadInteractionQueryKey,
  getThreadQueryKey,
} from "hooks/useThread";
import { useRouter } from "next/router";
import { Filters } from "store/slices/search";

/**
 * `useForkThread` allows the current thread to be forked optimistically.
 * - Adds a loading interaction to the original thread's cache.
 * - Replaces it on success with a redirect to the new thread.
 * - Cleans up the optimistic state on error.
 */
export function useForkThread(threadId?: string) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { isSignedIn, isLoaded } = useAuth();
  const { isProToggleOn } = useProToggle();

  const { currentSearchMode } = useSearchMode();

  const THREAD_QUERY_KEY = getThreadQueryKey(threadId);
  const ADD_INTERACTION_KEY = getAddThreadInteractionQueryKey(threadId);

  const forkThreadMutation = useMutation(
    async ({
      userMessage,
      filters,
      overrides,
    }: {
      userMessage: string;
      filters: FilterParams;
      overrides?: ThreadInteractionOverrides;
    }) => {
      if (threadId === undefined) throw "ThreadId is undefined";
      const response = await forkThreadAPI({
        threadId: threadId,
        userMessage,
        isProEnabled: isProToggleOn ?? false,
        filters,
        overrides,
        searchMode: currentSearchMode,
      });
      return parseThreadResponse(response);
    },
    {
      mutationKey: ADD_INTERACTION_KEY,

      onMutate: async ({ userMessage }) => {
        await queryClient.cancelQueries(THREAD_QUERY_KEY);

        const optimisticInteraction: ThreadInteraction = {
          id: String(Date.now()), // Temporary client-only ID
          user_message: userMessage,
          is_pro_enabled: isProToggleOn ?? false,
          created_at: new Date().toISOString(),
          isLoading: true,
          mode: ProAgentMode.CONVERSATIONAL,
        };

        const previousData = queryClient.getQueryData<Thread>(THREAD_QUERY_KEY);

        queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            interactions: [...oldData.interactions, optimisticInteraction],
          };
        });

        return {
          previousData,
          optimisticInteractionId: optimisticInteraction.id,
        };
      },

      onSuccess: (newThread, _, context: any) => {
        // Clear the optimistic interaction from the original thread
        queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
          if (!oldData || !context) return oldData;
          return {
            ...oldData,
            interactions: oldData.interactions.filter(
              (i) => i.id !== context.optimisticInteractionId
            ),
          };
        });

        // Cache the newly forked thread
        queryClient.setQueryData(
          getThreadQueryKey(newThread.thread_id),
          newThread
        );

        // Update the sidebar thread list
        queryClient.invalidateQueries({
          queryKey: THREADS_QUERY_KEY,
          exact: true,
        });

        // redirect to the newly created thread
        router.push(
          threadPath(newThread.thread_id, newThread.title),
          undefined,
          { scroll: false }
        );
      },

      onError: (_err, _vars, context: any) => {
        // Remove the optimistic interaction if something goes wrong
        if (!context) return;
        queryClient.setQueryData<Thread>(THREAD_QUERY_KEY, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            interactions: oldData.interactions.filter(
              (i) => i.id !== context.optimisticInteractionId
            ),
          };
        });
      },
    }
  );

  const forkThread = async ({
    userMessage,
    filters,
    overrides,
  }: {
    userMessage: string;
    filters: Filters;
    overrides?: ThreadInteractionOverrides;
  }) => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      const redirect = `/#/?redirect_url=${router?.asPath}`;

      router.push(`${path.SIGN_IN}${redirect}`);
      return;
    }

    // API only accepts "day" units for duration, so convert it before passing.
    const convertedFilters = convertDurationToDays(filters);

    await forkThreadMutation.mutateAsync({
      userMessage,
      filters: getQueryFromFilters(convertedFilters),
      overrides,
    });
  };

  return {
    isForking: forkThreadMutation.isLoading,
    error: forkThreadMutation.error as Error | null,
    forkThread,
  };
}
